import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, Send, X } from "lucide-react";
import { API_BASE, formatApiError } from "./AuthContext";
import { fmtAUD } from "./EngagementSignView";

const fyOptions = (current) => {
  const start = parseInt(current.slice(2, 6), 10);
  return [start - 1, start, start + 1].map((y) => `FY${y}-${String(y + 1).slice(-2)}`);
};

function ItemRow({ it, i, onChange, onRemove }) {
  const eff = it.fee === null || it.fee === undefined || it.fee === "" ? Number(it.standard_fee || 0) : Number(it.fee);
  const overridden = it.fee !== null && it.fee !== undefined && it.fee !== "" && Math.abs(Number(it.fee) - Number(it.standard_fee || 0)) > 0.004;
  const cls = "bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px] focus:outline-none focus:border-[var(--ink)]";
  return (
    <tr className="border-b border-[var(--border)]" data-testid={`engagement-item-${i}`}>
      <td className="py-1.5 pr-2"><input className={`${cls} w-full`} value={it.name} onChange={(e) => onChange({ ...it, name: e.target.value })} data-testid={`engagement-item-name-${i}`} /></td>
      <td className="py-1.5 pr-2 text-right font-mono text-[12px] text-[var(--ink-3)] whitespace-nowrap">{fmtAUD(it.standard_fee)}</td>
      <td className="py-1.5 pr-2">
        <input type="number" min="0" step="0.01" className={`${cls} w-24 text-right`} placeholder={String(it.standard_fee || 0)}
          value={it.fee ?? ""} onChange={(e) => onChange({ ...it, fee: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`engagement-item-fee-${i}`} />
        {overridden && <div className="text-[9px] uppercase tracking-wider text-[var(--accent)] mt-0.5" data-testid={`engagement-item-overridden-${i}`}>Override</div>}
      </td>
      <td className="py-1.5 text-right font-mono text-[12px]">{fmtAUD(eff)}</td>
      <td className="py-1.5 pl-2"><button onClick={onRemove} className="text-[var(--ink-3)] hover:text-[#B91C1C]" data-testid={`engagement-item-remove-${i}`}><Trash2 size={12} /></button></td>
    </tr>
  );
}

// v68.51 — create / edit a draft engagement letter priced from workflow fees.
export default function EngagementEditor({ clientId, initial, onClose, onSaved }) {
  const [pricing, setPricing] = useState(null);
  const [fy, setFy] = useState(initial?.financial_year || "");
  const [items, setItems] = useState(initial?.items || []);
  const [body, setBody] = useState(initial?.letter_body || "");
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/engagements/pricing`).then((r) => {
      setPricing(r.data);
      setFy((v) => v || r.data.current_fy);
      setBody((v) => v || r.data.default_terms);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const addTemplate = () => {
    const t = pricing?.templates.find((x) => x.template_id === pick);
    if (!t) return;
    setItems((xs) => [...xs, { template_id: t.template_id, name: t.external_name || t.name, standard_fee: Number(t.fee || 0), fee: null }]);
    setPick("");
  };
  const total = items.reduce((s, it) => s + (it.fee === null || it.fee === undefined || it.fee === "" ? Number(it.standard_fee || 0) : Number(it.fee)), 0);

  const save = async (andSend) => {
    if (items.some((it) => !it.name.trim())) { toast.error("Every service needs a name"); return; }
    setBusy(true);
    try {
      const payload = { financial_year: fy, items, letter_body: body };
      const r = initial?.engagement_id
        ? await axios.patch(`${API_BASE}/engagements/${initial.engagement_id}`, payload)
        : await axios.post(`${API_BASE}/engagements`, { client_id: clientId, ...payload });
      if (andSend) {
        const s = await axios.post(`${API_BASE}/engagements/${r.data.engagement_id}/send`, {});
        toast.success(s.data.email_ok ? "Sent — the client can sign by email link or portal" : "Ready to sign — email didn't go out, copy the link instead");
      } else toast.success("Draft saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!pricing) return <div className="p-5 text-[12px] text-[var(--ink-3)]"><Loader2 size={12} className="inline animate-spin" /> Loading…</div>;
  return (
    <div className="card p-4 space-y-4 bg-[var(--subtle)]" data-testid="engagement-editor">
      <div className="flex items-center justify-between">
        <div className="overline">{initial?.engagement_id ? "Edit engagement letter" : "New engagement letter"}</div>
        <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="engagement-editor-close"><X size={14} /></button>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[11px] text-[var(--ink-3)]">Financial year</label>
        <select value={fy} onChange={(e) => setFy(e.target.value)} className="bg-white border border-[var(--border)] rounded px-2 py-1 text-[12px]" data-testid="engagement-fy-select">
          {fyOptions(pricing.current_fy).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <div>
        <div className="flex gap-2 mb-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1 bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px]" data-testid="engagement-template-select">
            <option value="">Add a service from your workflows…</option>
            {pricing.templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}{t.fee ? ` — ${fmtAUD(t.fee)}` : " — no fee set"}</option>)}
          </select>
          <button onClick={addTemplate} disabled={!pick} className="btn-secondary !py-1 text-[12px]" data-testid="engagement-template-add"><Plus size={12} /> Add</button>
          <button onClick={() => setItems((xs) => [...xs, { template_id: null, name: "", standard_fee: 0, fee: null }])} className="btn-secondary !py-1 text-[12px]" data-testid="engagement-custom-add">Custom line</button>
        </div>
        <table className="w-full">
          <thead><tr className="text-[9px] uppercase tracking-wider text-[var(--ink-3)] border-b border-[var(--border)]">
            <th className="text-left py-1">Service</th><th className="text-right py-1 pr-2">Standard</th><th className="text-left py-1">Your price</th><th className="text-right py-1">Fee</th><th /></tr></thead>
          <tbody>
            {items.map((it, i) => <ItemRow key={i} i={i} it={it} onChange={(n) => setItems((xs) => xs.map((x, j) => (j === i ? n : x)))} onRemove={() => setItems((xs) => xs.filter((_, j) => j !== i))} />)}
            {items.length === 0 && <tr><td colSpan={5} className="py-3 text-center text-[12px] text-[var(--ink-3)] italic">No services yet — add from your workflows.</td></tr>}
          </tbody>
        </table>
        {items.some((it) => it.template_id && !Number(it.standard_fee)) && (
          <div className="text-[11px] text-[#B45309] mt-2" data-testid="engagement-no-fee-hint">
            Some services have no standard fee yet — type your price here, or set it once in <a href="/workflows" target="_blank" rel="noreferrer" className="underline">Workflows</a>.
          </div>
        )}
        <div className="text-right text-[13px] mt-2">Total <span className="font-mono font-medium" data-testid="engagement-editor-total">{fmtAUD(total)}</span></div>
      </div>
      <div>
        <label className="overline block mb-1">Letter wording</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} data-testid="engagement-letter-input"
          className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[12px] font-mono focus:outline-none focus:border-[var(--ink)]" />
        <div className="text-[10px] text-[var(--ink-3)] mt-1">Placeholders: {"{client_name}"} {"{firm_name}"} {"{financial_year}"}</div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={() => save(false)} disabled={busy} className="btn-secondary text-[12px]" data-testid="engagement-save-draft"><Save size={12} /> Save draft</button>
        <button onClick={() => save(true)} disabled={busy || !items.length} className="btn-primary text-[12px]" data-testid="engagement-save-send">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Save & send to client
        </button>
      </div>
    </div>
  );
}
